import React, {useState, useEffect} from "react";

// utils
import moment from "moment";

// Redux
import {useSelector, useDispatch} from "react-redux";
import {
  fetchCoupon,
  editCoupon,
  deleteCoupon,
} from "../../redux/features/coupons/couponsSlice";

// Routing
import {useParams, useHistory, Redirect} from "react-router-dom";

// Components
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import Paper from "../UI/MainPaper";
import CouponsForm from "./CouponsForm";
import FullContainerProgress from "../UI/FullContainerProgress";
import DeleteButton from "../Buttons/DeleteButton";
import DeleteDialog from "../UI/DeleteDialog";
import BreadcrumbsWithEdit from "../Navigation/BreadcrumbsWithEdit";

const CouponsEdit = () => {
  // Local State
  const [isEditMode, setEditMode] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Routing
  const {id} = useParams();
  const history = useHistory();

  // Redux
  const dispatch = useDispatch();
  const {currentPageCoupon, isLoading, error} = useSelector(
    state => state.coupons
  );
  const {userPermission} = useSelector(state => {
    return state.auth;
  });
  const {write, delete: deletePermission} = userPermission.sales;

  const formattedValues = {
    name: currentPageCoupon.name,
    description: currentPageCoupon.description,
    coupon_code: currentPageCoupon.coupon_code,
    type: currentPageCoupon.type,
    discount_amount: currentPageCoupon.discount_amount,
    max_discount: currentPageCoupon.max_discount,
    min_order_total: currentPageCoupon.min_order_total,
    min_night_stay: currentPageCoupon.min_night_stay,
    min_people: currentPageCoupon.min_people,
    uses_left: currentPageCoupon.uses_left,
    start_date: moment(currentPageCoupon.start_date),
    end_date: moment(currentPageCoupon.end_date),
    booking_date_start: moment(currentPageCoupon.booking_date_start),
    booking_date_end: moment(currentPageCoupon.booking_date_end),
    enabled: currentPageCoupon.enabled,
    only_complete_payment: currentPageCoupon.only_complete_payment,
    multiple_uses_same_customer: currentPageCoupon.multiple_uses_same_customer,
    packages: currentPageCoupon.packages,
    hotels: currentPageCoupon.hotels,
    condos: currentPageCoupon.condos,
    rooms: currentPageCoupon.rooms,
  };

  let breadcrumbNameMap = {
    "/coupons": "Coupons",
  };
  breadcrumbNameMap[`/coupons/${id}`] = currentPageCoupon.name;

  useEffect(() => {
    // check if the id is a int to keep going
    // This if is why javascript is broken :)
    if (Number.isInteger(id * 1) && id * 1 < Number.MAX_SAFE_INTEGER) {
      dispatch(fetchCoupon(id));
    } else {
      history.push("/404");
    }
  }, [dispatch, history, id]);

  useEffect(() => {
    setEditMode(write);
  }, [write]);

  const onSubmit = async values => {
    const couponObj = {
      coupon_id: id,
      ...values,
    };
    // Logic to show error or success
    const res = await dispatch(editCoupon(couponObj));
    if (!res.error) {
      history.push({
        pathname: "/coupons",
        state: {
          showSuccess: true,
          snackbarContent: `Your changes to ${couponObj.name} were saved successfully!`,
          snackbarLink: `/coupons/${couponObj.coupon_id}`,
        },
      });
    }
  };

  const onDelete = async () => {
    // Logic to show error or success
    const res = await dispatch(deleteCoupon(id));
    if (!res.error) {
      history.push({
        pathname: "/coupons",
        state: {
          showSuccess: true,
          snackbarContent: `The Coupon was successfully deleted!`,
        },
      });
    }
  };

  // Redirect if coupon doesn't exist'
  if (error && error.errorCode === 404) {
    return <Redirect to={"/404"} />;
  }

  return (
    <>
      <Grid container spacing={2}>
        <BreadcrumbsWithEdit
          isLoading={isLoading}
          breadcrumbNameMap={breadcrumbNameMap}
          isEditMode={isEditMode}
          setEditMode={setEditMode}
          writePermission={write}
        />
        <Grid item xs={12}>
          <Paper>
            {isLoading ? (
              <FullContainerProgress />
            ) : (
              <>
                <Typography component="h1" variant="h5" gutterBottom>
                  {currentPageCoupon.name}
                </Typography>
                <CouponsForm
                  defaultValues={formattedValues}
                  readOnly={!isEditMode}
                  submitButtonLabel="Save Changes"
                  onSubmit={onSubmit}
                  onCancel={() => setEditMode(false)}
                />
              </>
            )}
          </Paper>
        </Grid>
        {isEditMode && !isLoading && !!deletePermission && (
          <Grid item xs={12}>
            <DeleteButton onClick={() => setDialogOpen(true)} size="large">
              Delete Coupon
            </DeleteButton>
          </Grid>
        )}
      </Grid>
      {!!deletePermission && (
        <DeleteDialog
          dialogTitle="Are you sure you want to delete this Coupon?"
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onSubmit={() => onDelete()}
        />
      )}
    </>
  );
};

export default CouponsEdit;
